<template>
  <main class="c-white bg-black">
    <!-- Hero -->
    <section class="flex justify-center relative">
      <base-picture v-bind="hero.image" class="w-full" />
      <div class="h-full w-10/12 top-0 absolute lg:ml-20 lg:w-2/5 md:ml-10 md:h-a md:w-3/4 md:left-0 md:top-1/2 md:-translate-y-1/2">
        <base-picture v-bind="hero.logo" class="mb-5 w-2/3 <md:mt-20 md:w-1/2" />
        <h1
          v-style:text-size="{ sm: '9.72vw', md: '3rem', lg: '4.06rem' }"
          v-html="hero.head"
          class="fw-medium lh-none mb-5"
        />
        <p
          v-style:text-size="{ sm: '4.44vw', md: '1.375rem' }"
          v-html="hero.body"
          class="text-md lh-tight md:text-xl md:mb-7"
        />
        <div class="c-black f-col gap-1 inset-x-0 bottom-0 <md:mb-4 <md:absolute md:w-max">
          <vf-button v-for="({ to, title }, key) in hero.buttons" :key :to variant="primary-inverse">
            <span>{{ $t[title] }}</span>
          </vf-button>
        </div>
      </div>

      <!-- 'Discover The Evolution' Arrow Animation -->
      <div v-style:mb="{ sm: '12rem', md: '.5rem' }" class="bottom-0 absolute lg:block md:hidden">
        <custom-arrow-scroll
          :size="{ sm: 'sm', md: 'md' }"
          @click="scrollToElement('navigation', { offset: 115 })"
        >
          {{ hero.scrolldown }}
        </custom-arrow-scroll>
      </div>
    </section>

    <!-- Navigation -->
    <section v-style:mb="{ sm: '5em', lg: '9.625rem' }" data-scroll-el="navigation">
      <div class="mb-10 f-col center md:mb-20">
        <div class="w-4/5 md:text-center md:w-11/12" style="max-width: 80rem">
          <h2
            v-style:text-size="{ sm: '7.22vw', md: '3.0rem' }"
            v-html="selector.head"
            class="fw-medium lh-tight mb-5 mt-20"
          />
          <p
            v-style:text-size="{ sm: '4.44vw', md: '1.5rem' }"
            v-html="selector.body"
            class="text-md lh-tight md:text-lg"
          />
        </div>
      </div>
      <base-carousel class="text-center flex justify-center">
        <div
          v-for="({ id, shoeImage, shoeImageBackground, title, description }, key) in shoes"
          :key
          class="group w-7/10 relative overflow-x-hidden lg:w-1/4 md:w-2/5"
        >
          <div class="mb-3 lg:mb-20">
            <base-picture v-bind="shoeImageBackground" />
            <base-picture v-bind="shoeImage" class="flex duration-500 center top-0 absolute group-hover:scale-120" />
          </div>
          <base-link class="subtitle-2 uppercase" :to="`#${id}`">
            <span class="fw-bold">Summit {{ title }}</span>
            <span v-if="id === 'summit-cragstone'"> Pro</span>
            <span v-else> Boot</span>
            <span class="absolute-0" />
          </base-link>
          <p>
            {{ description }}
          </p>
        </div>
      </base-carousel>
    </section>

    <!-- Boots -->
    <section v-for="(shoe, key) in shoes" :key :id="shoe.id">
      <div class="f-col w-full items-center">
        <!-- Shoe Name with CTAs -->
        <div class="f-col w-full items-center relative lg:mb-15 md:px-5 md:py-17">
          <div class="f-col w-full center <md:mb-8 lg:flex-row">
            <div class="mb-10 z-2 md:mb-0 md:flex md:w-1/2 md:center">
              <div class="relative">
                <base-picture v-bind="shoe.shoeImageBackground" />
                <base-link :to="shoe.buttons[0].to">
                  <base-picture
                    v-style="{
                      h: { md: '110%', lg: '130%' },
                      w: { md: '110%', lg: '130%' },
                    }"
                    v-bind="shoe.shoeImage"
                    class="full center-absolute"
                    fit="contain"
                  />
                </base-link>
              </div>
            </div>

            <div v-motion-fade-up-visible class="w-4/5 lg:mt-0 lg:w-1/2 md:mt-10 md:w-11/12">
              <div class="absolute-0 <md:w-5/6">
                <base-picture
                  v-bind="shoe.decoImg"
                  class="lg:hidden"
                  style="transform: translate(-30%, -50%)"
                />
                <base-picture
                  v-bind="shoe.decoImg"
                  class="<lg:hidden"
                  style="transform: translate(-40%, -40%)"
                />
              </div>

              <base-picture
                v-style:h="{ md: '13.75vw', lg: '7.5vw' }"
                v-bind="shoe.shoeNamePng"
                class="mb-6 relative md:mb-9"
                :style="{ 'aspect-ratio': shoe.shoeNamePng.width / shoe.shoeNamePng.height }"
              />
              <p
                v-style:text-size="{ sm: '3.61vw', md: '1.375rem', lg: '1.25rem' }"
                class="lh-tight mb-6 relative md:mb-9"
                style="max-width: 32.5rem"
              >
                <span v-html="shoe.shoeBody" class="fw-medium" /> <span v-html="shoe.shoeBody2" class="fw-light" />
              </p>
              <div class="c-black f-col gap-2 inset-x-0 relative md:w-max">
                <template v-for="({ to, title, ytVideo }, i) in shoe.buttons" :key="i">
                  <vf-button
                    v-if="ytVideo"
                    variant="tertiary-inverse"
                    @click="ModalYoutube.open(
                      {
                        videoUrl: `${ytVideo[hero.ytLanguage]}?autoplay=1`,
                        title,
                      }, {
                        styleContent: 'background: rgba(0, 0, 0, 0.9); padding-block: 4.5rem;',
                      })"
                  >
                    {{ $t.videoPlay }}
                    <vf-icon name="play" size="md" />
                  </vf-button>
                  <vf-button v-else :to variant="primary-inverse">
                    <span>{{ $t[title] }}</span>
                  </vf-button>
                </template>
              </div>
            </div>
          </div>

          <div v-style:w="{ sm: '80%', md: '81vw', lg: '28.125vw' }" class="mb-15 mt-8 bottom-0 right-0 lg:mb-2 lg:absolute">
            <base-picture v-bind="shoe.mountainAnim" class="mountain-anim-container mb-10 md:hidden" :data-attr-mountain-url="shoe.mountainAnim.src.sm" />
            <base-picture v-bind="shoe.mountainAnim" class="mountain-anim-container <md:hidden" :data-attr-mountain-url="shoe.mountainAnim.src.md" />
            <div class="lh-snug right-0 top-0 lg:mr-8 lg:mt-2 lg:w-2/5 lg:absolute md:mt-20">
              <p
                v-for="{ attribute, value } in shoe.mountain"
                :key="attribute"
                v-style:text-size="{ sm: '3.61vw', md: '1.125rem', lg: '0.82vw' }"
                class="fw-light lh-snug"
              >
                <span class="fw-medium uppercase">{{ attribute }}</span> {{ value }}
              </p>
            </div>
          </div>
        </div>

        <!-- Carousel Containing Material Details -->
        <div class="flex center md:mb-15 md:relative">
          <base-carousel class="text-center <md:ml-5">
            <div
              v-for="({ image, caption }, i) in shoe.slider"
              :key="i"
              class="px-5 w-11/12 lg:w-1/3 md:w-1/2"
            >
              <div class="relative z-2">
                <base-picture v-bind="image" />
                <p
                  v-style:text-size="{ sm: '3.19vw', md: '1.125rem' }"
                  v-html="caption"
                  class="lh-tight mb-5 px-4 bottom-0 absolute z-1"
                />
              </div>
            </div>
          </base-carousel>
        </div>

        <base-picture v-bind="shoe.divider.image" class="md:mb-15" />

        <!-- Inner Material Details w/ Videos -->
        <template v-if="shoe.id === 'summit-breithorn'">
          <div class="mb-15 relative lg:hidden" :style="{ background: `url(${shoe.details.image.src}) center 120vw / 100% auto no-repeat` }">
            <div class="f-col center relative">
              <base-video v-bind="shoe.details.video" :class="shoe.details.video1ContainerDimensions" />
              <div class="p-10 text-center">
                <h3 v-style:text-size="titleTextSize" v-html="shoe.details.head" class="mb-4" :class="titleTextClasses" />
                <p v-style:text-size="paragraphTextSize" v-html="shoe.details.body" class="md:w-4/5" :class="paragraphTextClasses" />
              </div>
              <base-video v-bind="shoe.details.video2" :class="shoe.details.video2ContainerDimensions" />
            </div>
          </div>
          <div class="mb-10 relative <lg:hidden">
            <base-picture v-bind="shoe.details.image" />
            <div class="w-4/5 center-absolute">
              <div class="flex items-center relative between">
                <base-video v-bind="shoe.details.video" :class="shoe.details.video1ContainerDimensions" />
                <base-video v-bind="shoe.details.video2" :class="shoe.details.video2ContainerDimensions" />
              </div>
              <div class="text-center center-absolute">
                <h3 v-html="shoe.details.head" class="mb-8" :class="titleTextClasses" :style="titleTextStyles" />
                <p v-html="shoe.details.body" class="text-2xl w-3/4" :class="paragraphTextClasses" />
              </div>
            </div>
          </div>
        </template>

        <div v-else class="w-full relative <lg:mb-15" :class="shoe.details.imageContainerProperties">
          <base-picture v-if="shoe.details.image" v-bind="shoe.details.image" class="w-full" />
          <div class="w-full lg:flex lg:center" :class="shoe.details.position">
            <div class="f-col center relative lg:flex-row lg:justify-around">
              <template v-if="shoe.details.video.sm">
                <base-video
                  v-if="shoe.details.video.sm"
                  v-bind="shoe.details.video.sm"
                  class="lg:hidden"
                  :class="shoe.details.video1ContainerDimensions"
                />
                <base-video
                  v-if="shoe.details.video.md"
                  v-bind="shoe.details.video.md"
                  class="mb-15 <lg:hidden"
                  :class="shoe.details.video1ContainerDimensions"
                />
              </template>
              <base-video v-else v-bind="shoe.details.video" :class="shoe.details.video1ContainerDimensions" />
              <div class="p-10 text-center lg:hidden">
                <h3 v-style:text-size="titleTextSize" v-html="shoe.details.head" class="mb-4" :class="titleTextClasses" />
                <p v-style:text-size="paragraphTextSize" v-html="shoe.details.body" class="lg:w-a md:w-4/5" :class="paragraphTextClasses" />
              </div>
              <base-video v-bind="shoe.details.video2" :class="shoe.details.video2ContainerDimensions" />
            </div>
            <div class="text-center <lg:hidden center-absolute">
              <h3 v-html="shoe.details.head" class="mb-8" :class="titleTextClasses" :style="titleTextStyles" />
              <p class="text-2xl w-3/4" :class="paragraphTextClasses">
                {{ shoe.details.body }}
              </p>
            </div>
          </div>
        </div>

        <!-- Carousel: "Athlete Tested. Expedition Proven." -->
        <div class="w-full relative <md:mb-5">
          <base-picture
            v-style:w="{ sm: '60vw', lg: '33.56vw' }"
            v-bind="selector.logo"
            class="ml-6 mt-10 top-0 absolute z-2 lg:ml-20 lg:mt-0 lg:top-1/2 lg:-translate-y-1/2 md:ml-12 md:mt-20"
            :style="backgroundGradient"
          />
          <div class="text-center flex gap-5 select-none overflow-hidden">
            <div class="flex gap-5 justify-around md:gap-15" style="animation: animation-scroll 150s linear infinite">
              <template v-for="index in 3" :key="index">
                <div
                  v-for="({ image, name, occupation }, j) in shoe.gallery"
                  :key="j"
                  v-style:w="{ sm: '18.43rem', md: '38.75rem', lg: '28.44rem' }"
                >
                  <div class="relative">
                    <base-picture v-bind="image" class="relative" />
                    <div
                      class="p-2 w-full uppercase bottom-0 left-1/2 absolute md:p-8 -translate-x-1/2"
                      style="background: linear-gradient(to bottom, transparent, #00000095)"
                    >
                      <p v-style:text-size="{ sm: '3.33vw', md: '1.5rem' }" class="lh-tight">
                        <span class="fw-bold">{{ name }}</span><br>{{ occupation }}
                      </p>
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </div>

        <base-picture v-bind="shoe.divider.image" class="md:my-15" />

        <!-- Video Collage (Section Footer) -->
        <div class="mb-5 flex w-full justify-center relative lg:mb-0 md:mb-10">
          <div v-if="shoe.id === 'summit-cayesh'" class="relative">
            <base-picture v-bind="shoe.videos.background" />
            <div class="px-5 w-11/12 absolute center-absolute">
              <div class="f-col center relative lg:flex-row lg:between">
                <base-video v-bind="shoe.videos.video" class="md:7/10 w-4/5 lg:w-2/5" />
                <div class="py-10 text-center lg:hidden" :style="backgroundGradient">
                  <h3 v-style:text-size="titleTextSize" v-html="shoe.videos.head" class="mb-4" :class="titleTextClasses" />
                  <p v-style:text-size="paragraphTextSize" v-html="shoe.videos.body" class="lg:w-a md:w-4/5" :class="paragraphTextClasses" />
                </div>
                <base-picture
                  v-style="{
                    m: { lg: '0 14rem 0 0' },
                    w: { sm: '6.25rem', md: '12.5rem', lg: '7rem' },
                  }"
                  v-bind="shoe.videos.image"
                />
              </div>
              <div class="text-center <lg:hidden center-absolute" :style="backgroundGradient">
                <h3 v-html="shoe.videos.head" class="mb-8" :class="titleTextClasses" :style="titleTextStyles" />
                <p class="text-2xl w-3/4" :class="paragraphTextClasses">
                  {{ shoe.videos.body }}
                </p>
              </div>
            </div>
          </div>

          <div v-else-if="shoe.id === 'summit-breithorn'" class="mb-5 f-col center relative lg:mb-20 lg:flex-row lg:w-4/5 lg:between">
            <base-video v-bind="shoe.videos.video" class="w-4/5 lg:w-1/2" />
            <div class="py-10 text-center f-col w-4/5 center lg:hidden">
              <h3 v-style:text-size="titleTextSize" v-html="shoe.videos.head" class="mb-4" :class="titleTextClasses" />
              <p v-style:text-size="paragraphTextSize" v-html="shoe.videos.body" class="lg:w-a md:w-4/5" :class="paragraphTextClasses" />
            </div>
            <base-video v-if="shoe.videos.video2" v-bind="shoe.videos.video2" class="w-2/3 lg:w-1/3" />
            <div class="text-center <lg:hidden center-absolute" :style="backgroundGradient">
              <h3 v-html="shoe.videos.head" class="mb-8" :class="titleTextClasses" :style="titleTextStyles" />
              <p class="text-2xl w-3/4" :class="paragraphTextClasses">
                {{ shoe.videos.body }}
              </p>
            </div>
          </div>

          <div v-else class="f-col center relative <md:mb-5 md:flex-row md:between" :class="shoe.videos.videoContainerProperties">
            <base-video v-if="shoe.videos.video.sm" v-bind="shoe.videos.video.sm" class="lg:hidden" />
            <base-video v-if="shoe.videos.video.lg" v-bind="shoe.videos.video.lg" class="<lg:hidden" />
            <div
              v-if="shoe.id === 'summit-cragstone'"
              v-style:p="{ sm: '0 0 2rem 0', md: '0 0 5.25rem 0', lg: '0' }"
              class="text-center w-4/5 lg:w-1/3 center-absolute"
              :style="backgroundGradient"
            >
              <h3 v-style:text-size="titleTextSize" v-html="shoe.videos.head" class="mb-4 lg:mb-7" :class="titleTextClasses" />
              <p v-style:text-size="paragraphTextSize" :class="paragraphTextClasses">
                {{ shoe.videos.body }}
              </p>
            </div>

            <div v-else class="text-center w-4/5 lg:w-1/3 center-absolute" :style="backgroundGradient">
              <h3 v-style:text-size="titleTextSize" v-html="shoe.videos.head" class="mb-4 lg:mb-7" :class="titleTextClasses" />
              <p v-style:text-size="paragraphTextSize" :class="paragraphTextClasses">
                {{ shoe.videos.body }}
              </p>
            </div>
          </div>
        </div>

        <base-picture v-if="shoe.id === `summit-torre-egger`" v-style:mb="{ sm: '5rem', lg: '10rem' }" v-bind="shoe.divider.image" />
        <base-picture
          v-else-if="shoe.id === 'summit-cragstone'"
          v-style:mb="{ sm: '.25rem', md: '5rem', lg: '4.5rem' }"
          v-bind="shoe.divider.image"
          class="lg:hidden"
        />
        <base-picture v-else v-style:mb="{ sm: '.25rem', md: '5rem', lg: '7.5rem' }" v-bind="shoe.divider.image" />
      </div>
    </section>

    <!-- 'Back' Arrow Animation -->
    <custom-arrow-scroll
      class="mb-6 flex justify-center md:mb-20"
      dir="up"
      :size="{ sm: 'sm', md: 'md' }"
      @click="scrollToElement('navigation', { offset: 115 })"
    >
      {{ $t.back }}
    </custom-arrow-scroll>

    <modal-youtube v-slot="attrs" hide-close-button size="md">
      <dialog-youtube v-bind="attrs" button-variant="primary" class-button="<md:p-1" class-content="w-full" />
    </modal-youtube>
  </main>
</template>

<script lang="ts" setup>
definePageMeta({ header: { transparent: true } })

const ModalYoutube = createModal('youtube')
const { $t } = useNuxtApp()
const { data, error } = await useApi().internal.getStaticPage('summit-series-footwear')
if (!data.value) throw createError({ ...error.value!, fatal: true })

const { hero, selector, shoes } = data.value

const backgroundGradient = 'background: radial-gradient(closest-side, #00000070, transparent)'
const paragraphTextClasses = 'm-a lh-tight'
const paragraphTextSize = { sm: '3.61vw', md: '1.5rem' }
const titleTextClasses = 'fw-light uppercase'
const titleTextSize = { sm: '5.83vw', md: '2.25rem' }
const titleTextStyles = 'font-size: 2.25rem'

onMounted(() => {
  const allMountains = document.querySelectorAll('.mountain-anim-container')

  allMountains.forEach((element) => {
    let gifStarted = false

    element.addEventListener('mouseenter', () => {
      if (!gifStarted) {
        const mountainImage = element.querySelector('img')
        if (mountainImage) {
          mountainImage.src = `${element.getAttribute('data-attr-mountain-url')}?${new Date().getTime()}`
          gifStarted = true
          setTimeout(() => {
            gifStarted = false
          }, 4000)
        }
      }
    })
  })
})
</script>
